import {
    RestApiDayAvailabilityDto,
    RestApiDeliveryMethodAreaDto,
    RestApiDeliveryMethodDto,
    RestApiPriceValueDto,
} from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { useCallback, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
    useCartStore,
    CartContext,
    matchTimeBlockedAreaInMethod,
    resolveDeliveryZoneTime,
} from '@/entities/cart';
import { VenueAvailabilityKind } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { isAvailableNow } from './useTimeAvailability';

export type DeliveryMethodLike = RestApiDeliveryMethodDto & {
    timeAvailability?: RestApiDayAvailabilityDto[];
};

export type Coords = { lat: number; lon: number } | null;

export type DeliveryMethodAvailability = {
    isTimeRestricted: boolean;
    isMinOrderDisabled: boolean;
    isAddressRequired: boolean;
    isUndeliverable: boolean;
    /** Any reason the method cannot be used right now (incl. missing address). */
    isDisabled: boolean;
    /**
     * Whether the method may be auto-selected. A method whose deliverability is
     * merely unknown because no address is entered yet (`isAddressRequired`) is
     * still selectable — the badge and the order-submit guard handle that state.
     */
    isSelectable: boolean;
    minPrice: RestApiPriceValueDto | null;
    matchedArea: RestApiDeliveryMethodAreaDto | null;
    /**
     * Short badge copy when the address sits in a zone its time plan blocks right now,
     * while the restaurant itself is open. Null when the address is outside every zone,
     * which stays the generic undeliverable message. The confirm-order toast uses a longer
     * sentence when this is set.
     */
    areaTimeRestrictionLabel: string | null;
};

/**
 * Single source of truth for "can this delivery/pickup method be used right now".
 * Centralizes the time-restriction, minimum-order, address-required and
 * out-of-zone checks that the cart delivery box, the method list, the order
 * summary guard and the provider fallback all need.
 *
 * Coordinates default to the address currently in the cart form; pass an
 * explicit `coords` to evaluate against a different address (e.g. while
 * switching delivery type before the form address is updated), or `null` to
 * evaluate as if no address were entered.
 */
export const useDeliveryMethodAvailability = () => {
    const t = useTranslations();
    const isVenueOpen = useVenueAvailabilityStore((s) => s.status.kind === VenueAvailabilityKind.Open);
    const getDeliveryMethodArea = useCartStore((s) => s.getDeliveryMethodArea);
    const getMinOrderRestriction = useCartStore((s) => s.getMinOrderRestriction);
    const { formMethods } = useContext(CartContext);
    const [lat, lon, time] = useWatch({
        control: formMethods.control,
        name: ['deliveryMethod.address.lat', 'deliveryMethod.address.lon', 'time'],
    });
    const zoneTime = useMemo(() => resolveDeliveryZoneTime(time), [time]);

    const formCoords = useMemo<Coords>(() => {
        const parsedLat = parseFloat(lat ?? '');
        const parsedLon = parseFloat(lon ?? '');
        return Number.isFinite(parsedLat) && Number.isFinite(parsedLon)
            ? { lat: parsedLat, lon: parsedLon }
            : null;
    }, [lat, lon]);

    const getAvailability = useCallback(
        (method: DeliveryMethodLike, coordsOverride?: Coords): DeliveryMethodAvailability => {
            const coords = coordsOverride === undefined ? formCoords : coordsOverride;
            const isTimeRestricted = !isAvailableNow(method.timeAvailability ?? []);
            const { minPrice, isDisabled: isMinOrderDisabled } = getMinOrderRestriction(method);
            const hasAreas = !!method.areas?.length;
            const isAddressRequired = hasAreas && !coords;
            const matchedArea =
                hasAreas && coords ? getDeliveryMethodArea(method, coords.lat, coords.lon) : null;
            const isUndeliverable =
                hasAreas &&
                !isAddressRequired &&
                (matchedArea === null || matchedArea.deliveryAvailable === false);
            const isDisabled = isTimeRestricted || isMinOrderDisabled || isAddressRequired || isUndeliverable;
            const isSelectable = !isTimeRestricted && !isMinOrderDisabled && !isUndeliverable;

            const timeBlockedArea =
                isVenueOpen && isUndeliverable && matchedArea === null && coords
                    ? matchTimeBlockedAreaInMethod(method, coords, zoneTime)
                    : null;

            return {
                isTimeRestricted,
                isMinOrderDisabled,
                isAddressRequired,
                isUndeliverable,
                isDisabled,
                isSelectable,
                minPrice,
                matchedArea,
                areaTimeRestrictionLabel: timeBlockedArea
                    ? t('CART_DELIVERY_BOX.AREA_TIME_RESTRICTED')
                    : null,
            };
        },
        [formCoords, getMinOrderRestriction, getDeliveryMethodArea, isVenueOpen, zoneTime, t],
    );

    const findFirstSelectable = useCallback(
        <T extends DeliveryMethodLike>(methods: T[], coordsOverride?: Coords): T | undefined =>
            methods.find((method) => getAvailability(method, coordsOverride).isSelectable) ?? methods[0],
        [getAvailability],
    );

    return { getAvailability, findFirstSelectable };
};
